// Webplayer-specific Sentry initialisation.
//
// Reads VITE_SENTRY_DSN_WEBPLAYER from Cloudflare Pages env. When unset,
// initSentry() is a no-op — local dev and self-hosted instances pay no
// cost without explicit opt-in.
//
// Source maps must be enabled in vite.config.ts (build.sourcemap: true)
// for stack traces in the Sentry UI to be readable. They're served as
// static assets from Cloudflare Pages alongside the bundle; Sentry fetches
// them on demand. Trade-off: anyone can read the source maps from prod.
// For webplayer this is acceptable (logic is straightforward and runs on
// customer-facing displays, not behind auth).
//
// Session Replay is intentionally NOT enabled here. Webplayer DOMs are
// just video/menu rendering cycling through content — replays would be
// 30 seconds of "the next slide loaded" with no debugging value.

import * as Sentry from '@sentry/react'

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN_WEBPLAYER as string | undefined
  if (!dsn) return

  try {
    Sentry.init({
      dsn,
      environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) || 'production',
      // Cloudflare Pages injects CF_PAGES_COMMIT_SHA at build time; the
      // value is also exposed at runtime by some templates. We read it
      // through a Vite-injected var so it's pinned at build, matching the
      // bundle that was actually shipped.
      release: (import.meta.env.VITE_RELEASE as string) || undefined,

      // Performance traces — 10% sample matches the backend setting. Webplayer
      // has very few "transactions" (mostly initial load + heartbeat polls),
      // so this generates manageable volume.
      tracesSampleRate: 0.1,

      // Don't ship default PII; webplayer doesn't authenticate end-consumers
      // but request headers may carry the device_token cookie/header.
      sendDefaultPii: false,

      integrations: [
        Sentry.browserTracingIntegration(),
        // Replay deliberately omitted — see file header.
      ],

      // Filter out known low-value noise. Add to this list as patterns emerge
      // from the first few weeks in production.
      ignoreErrors: [
        // ResizeObserver loop warnings are benign in nearly every web app
        // and burn quota with no signal.
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications.',
      ],

      beforeSend(event) {
        if (event.request?.url) {
          event.request.url = event.request.url.replace(
            /\/devices\/content\/[a-f0-9]{16,}/gi,
            '/devices/content/[redacted]',
          )
        }
        if (event.message) {
          event.message = scrubDeviceToken(event.message)
        }
        if (event.exception?.values) {
          for (const value of event.exception.values) {
            if (value.value) value.value = scrubDeviceToken(value.value)
          }
        }
        return event
      },
    })
  } catch (e) {
    // Sentry init must never break the player. If something's wrong with
    // the DSN format, just log and continue.
    // eslint-disable-next-line no-console
    console.error('[sentry] init failed', e)
  }
}

function scrubDeviceToken(value: string): string {
  return value
    .replace(/[a-f0-9]{64}/gi, '[device-token]')
    .replace(/device_token["'=:\s]+[a-zA-Z0-9._-]+/gi, 'device_token=[redacted]')
}
